import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText, Badge, FormGroup } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiHash, FiUser, FiPackage, FiPercent } from "react-icons/fi";

// MUI DatePicker Imports for consistency
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const AddPurchase = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#2e4a3d' : '#dee2e6',
            boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(46, 74, 61, 0.25)' : 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const [farmers, setFarmers] = useState([]);
    const [products, setProducts] = useState([]);

    // 🔥 Track selected vendor details dynamically to check type (farmer vs trader)
    const [selectedVendorType, setSelectedVendorType] = useState('farmer');

    const [formData, setFormData] = useState({
        purchase_no: "",
        purchase_date: dayjs().format('YYYY-MM-DD'),
        farmer_id: "",
        product_id: "",
        rate: "",
        bag_weight: "0",
        gst_percentage: "0" // 🔥 New form key added to sync up with PHP dynamic inputs
    });

    const [sacks, setSacks] = useState([""]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const loadInitialData = async () => {
            dispatch(loadingTrue());
            try {

                const [farmerRes, productRes] = await Promise.all([
                    Handler({ method: "GET", url: "/farmer/list.php?limit=0" }),
                    Handler({ method: "GET", url: "/product/list.php" })
                ]);

                if (farmerRes?.success) {
                    setFarmers(
                        farmerRes.data?.map(f => ({
                            value: f.id,
                            label: `${f.contact?.name?.toUpperCase()} (${f.contact?.phone})`,
                            type: f.contact?.type || 'farmer'
                        }))
                    );
                }

                if (productRes?.success) {
                    setProducts(productRes.data?.map(p => ({ value: p.id, label: p.name?.toUpperCase() })));
                }
            } catch (err) {
                toast.error("Error loading selection data");
            } finally {
                dispatch(loadingFalse());
            }
        };
        loadInitialData();
    }, [dispatch]);

    const addSackField = () => setSacks([...sacks, ""]);
    const removeSackField = (index) => setSacks(sacks.filter((_, i) => i !== index));

    const handleSackChange = (index, value) => {
        const updatedSacks = [...sacks];
        updatedSacks[index] = value;
        setSacks(updatedSacks);
    };

    // Calculation Blocks
    const totalWeight = sacks.reduce((sum, w) => sum + (parseFloat(w) || 0), 0);
    const bagWt = parseFloat(formData.bag_weight) || 0;
    const netWeight = totalWeight - bagWt;
    const rateVal = parseFloat(formData.rate) || 0;

    // Core base computation matching backend variables 
    const totalAmount = netWeight > 0 ? netWeight * rateVal : 0;

    // 🔥 New Contextual GST computation blocks for handling real-time UI previews
    const gstPercentVal = selectedVendorType === 'trader' ? (parseFloat(formData.gst_percentage) || 0) : 0;
    const gstCalculatedAmount = (totalAmount * gstPercentVal) / 100;
    const finalBillAmountWithGst = totalAmount + gstCalculatedAmount;

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasEmptySack = sacks.some(s => s === "" || s === null || isNaN(s));

        const payload = {
            purchase_no: formData.purchase_no,
            purchase_date: formData.purchase_date,
            farmer_id: formData.farmer_id ? parseInt(formData.farmer_id) : null,
            product_id: parseInt(formData.product_id),
            rate: parseFloat(formData.rate),
            bag_weight: parseFloat(formData.bag_weight) || 0,
            sacks: sacks.map(s => parseFloat(s)),
            gst_percentage: selectedVendorType === 'trader' ? parseFloat(formData.gst_percentage) || 0 : 0 // 🔥 Dynamic allocation injection payload
        };

        /* Client Side Validation */
        if (!payload.purchase_no) return toast.error("Please enter Purchase Number");
        if (!payload.farmer_id) return toast.error("Please select a vendor profile");
        if (!payload.product_id) return toast.error("Please select a product item");
        if (!payload.rate || payload.rate <= 0) return toast.error("Please enter a valid rate metrics");
        if (hasEmptySack) return toast.error("Please enter weight for all sack fields or remove empty ones!");
        if (payload.sacks.length === 0) return toast.error("At least one sack weight entry is mandatory");

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/purchase/create.php",
                data: payload
            });

            if (res?.success) {
                Swal.fire({
                    title: "Success!",
                    text: `Purchase Ledger ${payload.purchase_no} recorded successfully`,
                    icon: "success",
                    confirmButtonColor: "#2e4a3d"
                });
                navigate("/purchases");
            } else {
                if (res?.errors) {
                    const firstErr = Object.values(res.errors)[0];
                    toast.error(firstErr);
                } else {
                    toast.error(res?.message || "Failed to create purchase entry");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Check Server Connection Lifecycle", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 p-md-4 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Purchase Registry Entry</h3>
                        <p className="text-muted small m-0">Cotton Mill Raw Material Sourcing System</p>
                    </div>
                    <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        <Col lg={8}>
                            {/* Entry Details Card - Manual No & Date */}
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h5 className="fw-bold mb-4 border-start border-4 border-success ps-3">Entry Metrics</h5>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold text-uppercase opacity-75">Purchase Invoice Reference No *</Label>
                                                <InputGroup className="overflow-hidden" style={{ borderRadius: '12px' }}>
                                                    <InputGroupText className="bg-white border-end-0"><FiHash /></InputGroupText>
                                                    <Input
                                                        className="border-start-0 ps-0 fw-bold"
                                                        style={{ height: '50px' }}
                                                        placeholder="Ex: PUR-2026-001"
                                                        value={formData.purchase_no}
                                                        onChange={(e) => setFormData({ ...formData, purchase_no: e.target.value })}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold text-uppercase opacity-75">Sourcing Timestamp Date *</Label>
                                                <DatePicker
                                                    disableFuture
                                                    format="DD/MM/YYYY"
                                                    value={dayjs(formData.purchase_date)}
                                                    onChange={(newValue) => {
                                                        setFormData({ ...formData, purchase_date: newValue ? newValue.format('YYYY-MM-DD') : '' });
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '12px',
                                                                    height: '50px',
                                                                    '& fieldset': { borderColor: '#dee2e6' },
                                                                    '&:hover fieldset': { borderColor: '#2e4a3d' },
                                                                },
                                                                '& .MuiInputBase-input': { fontWeight: 'bold' }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h5 className="fw-bold mb-4 border-start border-4 border-success ps-3">Vendor Account & Material Profile</h5>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Label className="small fw-bold text-uppercase opacity-75 m-0">Account Profile Target Holder *</Label>
                                                {formData.farmer_id && (
                                                    <Badge color={selectedVendorType === 'trader' ? 'warning' : 'success'} className="text-uppercase px-2 py-1 rounded-pill">
                                                        Classification: {selectedVendorType}
                                                    </Badge>
                                                )}
                                            </div>
                                            <Select
                                                options={farmers}
                                                isClearable
                                                styles={customSelectStyles}
                                                onInputChange={(val) => setSearchQuery(val)}
                                                onChange={(opt) => {
                                                    setFormData({ ...formData, farmer_id: opt?.value || "" });
                                                    setSelectedVendorType(opt ? opt.type : 'farmer'); // Hook type variable to state block on modification
                                                }}
                                                placeholder="Identify profile ledger..."
                                            />
                                        </Col>
                                        <Col md={selectedVendorType === 'trader' ? 4 : 6}>
                                            <Label className="small fw-bold text-uppercase opacity-75">Product Sourced Variety</Label>
                                            <Select
                                                styles={customSelectStyles}
                                                onInputChange={(val) => setSearchQuery(val)}
                                                options={products}
                                                onChange={(opt) => setFormData({ ...formData, product_id: opt?.value || "" })}
                                                placeholder="Choose structural variant..."
                                            />
                                        </Col>
                                        <Col md={selectedVendorType === 'trader' ? 4 : 6}>
                                            <Label className="small fw-bold text-uppercase opacity-75">Unit Sourcing Rate / KG</Label>
                                            <InputGroup size="lg">
                                                <InputGroupText className="bg-white border-end-0 text-success fw-bold" style={{ minHeight: '50px' }}>₹</InputGroupText>
                                                <Input
                                                    className="border-start-0 ps-0 fw-bold-custom"
                                                    style={{ height: '50px', fontWeight: 'bold' }}
                                                    type="number"
                                                    step="0.01"
                                                    onKeyDown={blockInvalidChar}
                                                    value={formData.rate}
                                                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                                    placeholder="0.00"
                                                />
                                            </InputGroup>
                                        </Col>

                                        {/* 🔥 Dynamic GST Form Field: Only shows if selected vendor is a Trader */}
                                        {selectedVendorType === 'trader' && (
                                            <Col md={4}>
                                                <Label className="small fw-bold text-uppercase opacity-75">Applicable GST Rate (%)</Label>
                                                <InputGroup size="lg">
                                                    <InputGroupText className="bg-white border-end-0 text-warning"><FiPercent /></InputGroupText>
                                                    <Input
                                                        className="border-start-0 ps-0 fw-bold"
                                                        style={{ height: '50px', fontWeight: 'bold' }}
                                                        type="number"
                                                        step="0.1"
                                                        onKeyDown={blockInvalidChar}
                                                        value={formData.gst_percentage}
                                                        onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                                                        placeholder="Ex: 5, 12, 18"
                                                    />
                                                </InputGroup>
                                            </Col>
                                        )}
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h5 className="fw-bold mb-0 border-start border-4 border-success ps-3">Raw Sacks Weight Breakdown</h5>
                                            <small className="text-muted ms-3">Log explicit metric configuration elements for mass tracking</small>
                                        </div>
                                        <Button color="success" size="sm" outline onClick={addSackField} className="rounded-pill px-3">
                                            <i className="bx bx-plus"></i> Add Sack Item
                                        </Button>
                                    </div>

                                    <div className="p-3 bg-light rounded-4 border border-dashed mb-2">
                                        <Row className="g-3">
                                            {sacks.map((sack, index) => (
                                                <Col xs={6} md={3} key={index}>
                                                    <InputGroup size="sm" className="shadow-sm rounded-3 overflow-hidden">
                                                        <InputGroupText className="bg-white border-end-0 text-muted small">{index + 1}</InputGroupText>
                                                        <Input
                                                            className="border-start-0 border-end-0 ps-0 fw-bold text-center"
                                                            type="number"
                                                            step="0.01"
                                                            onKeyDown={blockInvalidChar}
                                                            placeholder="0.00"
                                                            value={sack}
                                                            onChange={(e) => handleSackChange(index, e.target.value)}
                                                            style={{ height: '40px' }}
                                                        />
                                                        {sacks.length > 1 && (
                                                            <Button color="white" className="border-start shadow-none text-danger" onClick={() => removeSackField(index)}>
                                                                <i className="bx bx-trash-alt"></i>
                                                            </Button>
                                                        )}
                                                    </InputGroup>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top overflow-hidden" style={{ top: '100px' }}>
                                <div className="bg-theme p-4 text-white text-center" style={{ backgroundColor: '#2e4a3d' }}>
                                    <h6 className="opacity-75 text-uppercase small fw-bold mb-1">Final Settlement Payable</h6>
                                    <h2 className="fw-bold mb-0 text-warning">₹ {finalBillAmountWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                                </div>
                                <CardBody className="p-4">
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between mb-3">
                                            <span className="text-muted small fw-bold text-uppercase">Total Volume Count</span>
                                            <Badge color="dark" className="px-3 rounded-pill">{sacks.filter(s => s !== "").length} Sacks Loaded</Badge>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Gross Mass Weight:</span>
                                            <span className="fw-bold">{totalWeight.toFixed(2)} KG</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2 align-items-center">
                                            <span className="text-muted">Bag Tare Weight (Deduction):</span>
                                            <div style={{ width: '90px' }}>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-control-sm text-end fw-bold border-success"
                                                    value={formData.bag_weight}
                                                    onChange={(e) => setFormData({ ...formData, bag_weight: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between mb-3">
                                            <span className="text-muted">Net Chargeable Weight:</span>
                                            <span className="fw-bold text-dark">{netWeight > 0 ? netWeight.toFixed(2) : "0.00"} KG</span>
                                        </div>

                                        <hr className="my-2 opacity-10" />

                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Base Taxable Amount:</span>
                                            <span className="fw-bold">₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                        </div>

                                        {/* 🔥 Dynamic Tax display item for breaking down the billing state values */}
                                        {selectedVendorType === 'trader' && (
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">GST Breakup Tax ({formData.gst_percentage || 0}%):</span>
                                                <span className="fw-bold text-danger">+ ₹ {gstCalculatedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        )}

                                        <hr className="my-3 opacity-10" />
                                        <div className="d-flex justify-content-between fs-5">
                                            <span className="fw-bold text-dark">Total Consolidated:</span>
                                            <span className="fw-bold text-success">₹ {finalBillAmountWithGst > 0 ? finalBillAmountWithGst.toFixed(2) : "0.00"}</span>
                                        </div>
                                    </div>

                                    <Button color="success" block className="py-3 fw-bold rounded-4 shadow-sm w-100 mb-2" type="submit" style={{ background: '#2e4a3d', border: 'none' }}>
                                        <i className="bx bx-cloud-upload me-1"></i> LOG PURCHASE LEDGER
                                    </Button>
                                    <p className="text-center text-muted m-0" style={{ fontSize: '11px' }}>
                                        * System will lock inventory and map ledger entries automatically.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </LocalizationProvider>
    );
};

export default AddPurchase;